import { DestinationLocalHandlingProductCode, OriginLocalHandlingProductCode } from '@forto-dev/shipment-models'

export type LocalHandlingOption = {
  label: string
  value: string
}

export type LocalHandlingOptionsScope = 'origin_options' | 'destination_options'

// Note: i18next default namespace is `common`, so keys should be relative (no leading `common.`)
export const LOCAL_HANDLING_TRANSLATION_PREFIX = 'booking.wizard.create.extra_local_handling'

// Main carriage documentation dropdown values (as per temp_translation.json context)
export const MAIN_CARRIAGE_DOCUMENTATION_TYPES = ['COUO', 'VCO'] as const
export type MainCarriageDocumentationType = (typeof MAIN_CARRIAGE_DOCUMENTATION_TYPES)[number]

export const ORIGIN_LOCAL_HANDLING_VALUES = Object.values(OriginLocalHandlingProductCode)

type GetOriginLocalHandlingValuesInput = {
  /** VGM Weighing is offered for Sea FCL bookings, in every geography */
  isVgmWeighingApplicable: boolean
}

export const getOriginLocalHandlingValues = ({ isVgmWeighingApplicable }: GetOriginLocalHandlingValuesInput) =>
  ORIGIN_LOCAL_HANDLING_VALUES.filter((value) => {
    if (value === OriginLocalHandlingProductCode.VGMO) {
      return isVgmWeighingApplicable
    }

    // VGM Submission is attached by the backend and never shown to customers
    if (value === OriginLocalHandlingProductCode.VGMSO) {
      return false
    }

    // PLLABO is the retired typo of PALLBO, kept in the enum until the TTP-7907 cleanup
    if (value === OriginLocalHandlingProductCode.PLLABO) {
      return false
    }

    return true
  })

// The form keeps plain codes, so compare as strings
const VGMO_VALUE: string = OriginLocalHandlingProductCode.VGMO
const VGMSO_VALUE: string = OriginLocalHandlingProductCode.VGMSO

/** A VGM Weighing selection made on an earlier route has to go once it is no longer offered */
export const withoutVgmWeighingSelection = (selection: string[]): string[] =>
  selection.filter((value) => value !== VGMO_VALUE)

/**
 * VGM Submission is attached by the backend and never shown to a customer, so it has to be
 * filtered out of anything we render off a booking or a shipment.
 */
export const withoutCustomerHiddenProducts = <T extends { code: string }>(products: T[] | undefined): T[] =>
  (products ?? []).filter((product) => product.code !== VGMSO_VALUE)

export const DESTINATION_LOCAL_HANDLING_VALUES = Object.values(DestinationLocalHandlingProductCode)

const localHandlingValueToTranslationTerm = (value: string) => {
  return value.startsWith('_') ? value : value.toLowerCase()
}

export const getLocalHandlingTranslationKey = (
  scope: LocalHandlingOptionsScope,
  value: OriginLocalHandlingProductCode | DestinationLocalHandlingProductCode
) => {
  const term = localHandlingValueToTranslationTerm(value)
  return `${LOCAL_HANDLING_TRANSLATION_PREFIX}.${scope}.${term}`
}

export const buildLocalHandlingOptions = (
  translate: (key: string) => string,
  scope: LocalHandlingOptionsScope,
  values: readonly (OriginLocalHandlingProductCode | DestinationLocalHandlingProductCode)[]
): LocalHandlingOption[] => {
  return values.map((value) => {
    const key = getLocalHandlingTranslationKey(scope, value)
    return {
      value,
      label: translate(key),
    }
  })
}

export const getMainCarriageDocumentationTranslationKey = (value: string) => {
  const term = localHandlingValueToTranslationTerm(value)
  return `${LOCAL_HANDLING_TRANSLATION_PREFIX}.main_carriage_documentation.${term}`
}

export const buildMainCarriageDocumentationOptions = (
  translate: (key: string) => string,
  values: readonly string[]
): LocalHandlingOption[] => {
  return values.map((value) => {
    const key = getMainCarriageDocumentationTranslationKey(value)
    return {
      value,
      label: translate(key),
    }
  })
}
